import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import {
  CartridgeRoleAssignmentAggregation,
  CartridgeRoleAssignmentFlow,
  CartridgeWithSelectableRoles,
  Role,
  SelectableRole,
  VariantRoleAssignmentLimitResponse,
  VariantRoleLimitFilter,
} from '@markant-platform/authorizer/model';
import { UiCartridgeRolesAssignmentCardComponent } from '@markant-platform/shared/authorizer/ui';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { MarkantElementsModule } from '@markant/elements-angular';
import {
  SelectableSuborganization,
  SubOrgaExtraOptions,
  SubOrganizationData,
} from '@markant-platform/shared/organization/model';
import { AppConfigService, AppFeature, DEFAULT_REQ_PAGE_SIZE, Page } from '@markant-platform/shared/data-access';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { EditRoleSuborganizationAssignments } from '@markant-platform/shared/authorizer/model';
import { CartridgeRoleEditSuborganizationsDialogComponent } from '../cartridge-role-edit-suborganization-dialog/cartridge-role-edit-suborganizations-dialog.component';
import { ScrollIndicatorDirective } from '@markant-platform/shared/util-scroll';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY_PAGE } from '@markant-platform/shared/table/model';
import { CartridgeRoleAssignmentDataService } from '@markant-platform/shared/authorizer-utils';

@Component({
  selector: 'markant-platform-cartridge-role-assignment',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MarkantElementsModule,
    UiCartridgeRolesAssignmentCardComponent,
    CartridgeRoleEditSuborganizationsDialogComponent,
    ScrollIndicatorDirective,
  ],
  templateUrl: './cartridge-role-assignment.component.html',
  styleUrl: './cartridge-role-assignment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartridgeRoleAssignmentComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() groupName?: string;
  @Input() groupId?: string;
  @Input() userId?: string;

  suborganizations: SubOrganizationData[] = [];
  isEditSuborganizationDialogOpen = false;
  editRoleSuborganizationAssignments!: EditRoleSuborganizationAssignments;
  isLoading = true;
  hasSuborganizations = computed(() => this.cartridgeRoleAssignmentDataService.subOrganizations().length > 0);

  protected readonly CartridgeRoleAssignmentFlow = CartridgeRoleAssignmentFlow;
  readonly isRoleLimitEnabled = AppConfigService.isFeatureEnabled(AppFeature.ROLE_LIMITS);

  private readonly authorizerService = inject(AuthorizerService);
  private readonly organizationService = inject(OrganizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cartridgeRoleAssignmentDataService = inject(CartridgeRoleAssignmentDataService);

  protected selectedRoles = this.cartridgeRoleAssignmentDataService.selectedRoles;
  protected cartridgeSelectableRoles = this.cartridgeRoleAssignmentDataService.cartridgeRoles;
  protected searchTerm = signal<string>('');
  protected filteredCartridgeSelectableRoles: Signal<CartridgeWithSelectableRoles[]> = computed(() =>
    this.getFilterCartridgeRoles(this.cartridgeRoleAssignmentDataService.cartridgeRoles(), this.searchTerm())
  );

  ngOnInit(): void {
    Promise.all([this.loadAndSetCartridgeRolesSelectableData(), this.loadSuborganizations()]).then(() => {
      this.cartridgeRoleAssignmentDataService.initializeExistingRoles();
      this.isLoading = false;
    });
  }

  onRoleSearchChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm.toLowerCase());
  }

  onAddRole(role: Role): void {
    this.cartridgeRoleAssignmentDataService.addRole(role);
    this.cartridgeRoleAssignmentDataService.toggleRoleSelection(role.id, true);
    this.cartridgeRoleAssignmentDataService.decreaseSlots(role.cartridgeId);
  }

  onDeleteRole(role: SelectableRole): void {
    this.cartridgeRoleAssignmentDataService.deleteSelectedRole(role);
    this.cartridgeRoleAssignmentDataService.toggleRoleSelection(role.id, false);
    this.cartridgeRoleAssignmentDataService.increaseSlots(role.cartridgeId);
  }

  onDeleteAllRolesClick(): void {
    this.cartridgeRoleAssignmentDataService.freeAllVariantRoleLimitSlots();
    this.cartridgeRoleAssignmentDataService.deleteAllSelectedRoles();
    this.cartridgeRoleAssignmentDataService.unselectCartridgeRoles();
  }

  onSubOrganizationEditForRole(selectedRole: SelectableRole): void {
    const selectableSuborganizations = this.cartridgeRoleAssignmentDataService.subOrganizations().map(s => {
      return {
        ...s,
        selected: selectedRole.suborganizationData?.some(roleSuborganization => roleSuborganization.id === s.id),
      } as SelectableSuborganization;
    });
    this.isEditSuborganizationDialogOpen = true;
    this.editRoleSuborganizationAssignments = {
      role: selectedRole,
      suborganizations: selectableSuborganizations,
    };
  }

  onRoleSubOrganizationsChange(event: EditRoleSuborganizationAssignments): void {
    this.cartridgeRoleAssignmentDataService.updateSubOrganizations(event);
    this.isEditSuborganizationDialogOpen = false;
  }

  onCancelEditSuborganizations(): void {
    this.isEditSuborganizationDialogOpen = false;
  }

  private loadCartridgeBookedRoles(): Observable<Role[]> {
    return this.authorizerService.getAssignableDynamicRoles(this.organizationId).pipe(
      catchError(_ => of([])),
      map(roles => {
        roles.sort((a, b) => a.internalName.localeCompare(b.internalName));
        return roles;
      })
    );
  }

  private loadRoleAssignmentLimits(): Observable<VariantRoleAssignmentLimitResponse[]> {
    const queryParams: VariantRoleLimitFilter = {
      userId: this.userId,
      groupId: this.groupId,
    };
    return this.authorizerService
      .getRoleAssignmentLimits(this.organizationId, queryParams)
      .pipe(catchError(_ => of([])));
  }

  private async loadSuborganizations(): Promise<void> {
    if (!this.organizationId) {
      return;
    }

    let idx = 0;
    let pageResult;
    let subOrganizations: SubOrganizationData[] = [];
    do {
      pageResult = await this.loadSuborganizationPage(idx++);
      subOrganizations = subOrganizations.concat(pageResult.result);
    } while (pageResult.hasNext);
    this.cartridgeRoleAssignmentDataService.setSubOrganizations(subOrganizations);
  }

  private loadSuborganizationPage(pageIndex: number): Promise<Page<SubOrganizationData>> {
    return new Promise<Page<SubOrganizationData>>((resolve, reject) => {
      this.organizationService
        .getSubOrganizations(this.organizationId, pageIndex, DEFAULT_REQ_PAGE_SIZE, {
          flat: true,
        } as SubOrgaExtraOptions)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(err => {
            reject(err);
            return of(EMPTY_PAGE as Page<SubOrganizationData>);
          })
        )
        .subscribe(loadedSuborgas => resolve(loadedSuborgas));
    });
  }

  private loadAndSetCartridgeRolesSelectableData(): Promise<void> {
    return new Promise(resolve => {
      forkJoin({
        rawRoles: this.loadCartridgeBookedRoles(),
        roleLimits: this.loadRoleAssignmentLimits(),
      })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result: CartridgeRoleAssignmentAggregation) => {
            this.cartridgeRoleAssignmentDataService.initializeCartridgeRoles(result);
            resolve();
          },
        });
    });
  }

  private getFilterCartridgeRoles(
    cartridgeWithSelectableRoles: CartridgeWithSelectableRoles[],
    searchTerm: string
  ): CartridgeWithSelectableRoles[] {
    if (!searchTerm) {
      return cartridgeWithSelectableRoles;
    }

    const searchTermLowerCase = searchTerm.toLowerCase();

    return cartridgeWithSelectableRoles.reduce(
      (result: CartridgeWithSelectableRoles[], cartridgeWithSelectableRole: CartridgeWithSelectableRoles) => {
        const { name, id, roles } = cartridgeWithSelectableRole;
        const matchingCartridgeName =
          name.toLowerCase().includes(searchTermLowerCase) || id.toLowerCase().includes(searchTermLowerCase);

        if (matchingCartridgeName) {
          return [...result, cartridgeWithSelectableRole];
        }

        const filteredRoles = roles.filter(
          ({ localizedDisplayName, id }) =>
            localizedDisplayName.toLowerCase().includes(searchTermLowerCase) ||
            id.toLowerCase().includes(searchTermLowerCase)
        );

        if (!filteredRoles.length) {
          return result;
        }

        return [
          ...result,
          {
            ...cartridgeWithSelectableRole,
            roles: filteredRoles,
          },
        ];
      },
      []
    );
  }
}
